/*
 * Application Group area (audit Phase 3.3). Replaces the four legacy
 * wwwroot/js/AppGroup/*.js files. Reached from the Applications screen's
 * "Application Groups" button; the page is global, so its links must not get
 * the current domain id appended.
 *
 * The legacy module hung off the compat facade as Mcp.Admin.AppGroup.*. It is
 * a registered area now (AppGroup.*), like Person, Application and Domain, so
 * nothing here depends on the facade and the views name the handler directly.
 *
 * Three dead affordances are wired rather than restyled:
 *   - SetRowClickHandler bound row clicks on document-ready, which never runs
 *     for a partial swapped into the SPA shell. The rows carried
 *     cursor:pointer and did nothing; they are data-action rows now.
 *   - The Domain picker called $('#DomainSelectionForm').submit() on a form
 *     with no action and no method: the browser's native submit, a GET back to
 *     the current URL. It POSTs to AppGroup/List and swaps the partial now.
 *   - "Add Application" cloned the footer row into markup that named
 *     PersonClaimRow ids and set an onclick calling $(...).remove(selector),
 *     which is not what jQuery's remove() does. The clone is rebuilt here.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm } from '../core/modal';
import { attach, isValid } from '../core/validation';

const NEW_ROW = 'NewAppGroupAppRow';
const GRID = 'AppGroupApplicationsContainer';

function value(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
}

/**
 * The footer row's controls are bound as ApplicationsViewModel.Applications[n].
 * When no application is picked, rename them to names the model does not bind
 * so the empty row is neither posted back nor validated.
 */
function preventBlankRowPost(): void {
    const row = one(NEW_ROW);
    if (!row) return;
    const picked = (row.querySelector('.field-applicationid') as HTMLSelectElement | null)?.value;
    if (picked) return;
    const rename: Array<[string, string]> = [
        ['.field-appgroupid', 'newApplicationAppGroupId'],
        ['.field-applicationid', 'newApplicationApplicationId'],
        ['.field-resourcetype', 'newApplicationResourceType'],
    ];
    for (const [selector, name] of rename) {
        row.querySelector(selector)?.setAttribute('name', name);
    }
}

/** Re-index one cloned control onto ApplicationsViewModel.Applications[index]. */
function reindex(root: ParentNode, selector: string, field: string, index: number): Element | null {
    const control = root.querySelector(selector);
    if (!control) return null;
    control.setAttribute('name', `ApplicationsViewModel.Applications[${index}].${field}`);
    control.id = `ApplicationsViewModel_Applications_${index}_${field}`;
    return control;
}

export const Applications = {
    /**
     * First click reveals the footer entry row; each click after that lifts the
     * chosen application into the grid and resets the row for the next one.
     */
    AddRow(): false {
        const row = one(NEW_ROW) as HTMLTableRowElement | null;
        const grid = one(GRID);
        if (!row || !grid) return false;

        if (row.style.display === 'none' || row.classList.contains('d-none')) {
            row.style.display = '';
            row.classList.remove('d-none');
            return false;
        }

        const picker = row.querySelector('.field-applicationid') as HTMLSelectElement | null;
        if (!picker?.value) return false;                    // nothing chosen: leave the row open
        const index = Number.parseInt(row.getAttribute('data-id') ?? '0', 10) || 0;
        const name = picker.selectedOptions[0]?.textContent?.trim() ?? '';

        const groupId = (row.querySelector('.field-appgroupid') as HTMLInputElement | null)?.value ?? '';
        const resourceType = picker.selectedOptions[0]?.getAttribute('data-resourcetype') ?? '';

        const added = document.createElement('tr');
        added.id = `AppGroupAppRow${index}`;
        added.setAttribute('data-id', String(index));
        const field = `ApplicationsViewModel.Applications[${index}]`;
        const fieldId = `ApplicationsViewModel_Applications_${index}`;
        added.innerHTML =
            '<td>'
            + `<input type="hidden" class="field-appgroupid" name="${field}.AppGroupId" id="${fieldId}_AppGroupId" />`
            + `<input type="hidden" class="field-applicationid" name="${field}.ApplicationId" id="${fieldId}_ApplicationId" />`
            + `<input type="hidden" class="field-resourcetype" name="${field}.ResourceType" id="${fieldId}_ResourceType" />`
            + '<span class="field-applicationname"></span>'
            + '</td>'
            + '<td class="text-end">'
            + '<button type="button" class="btn btn-icon btn-icon-danger" title="Remove"'
            + ` data-action="AppGroup.Applications.RemoveRow" data-args='["#AppGroupAppRow${index}"]'>`
            + '<i class="fas fa-times"></i></button>'
            + '</td>';
        (added.querySelector('.field-appgroupid') as HTMLInputElement).value = groupId;
        (added.querySelector('.field-applicationid') as HTMLInputElement).value = picker.value;
        (added.querySelector('.field-resourcetype') as HTMLInputElement).value = resourceType;
        // textContent, never innerHTML: the label comes from server data and
        // this row is assembled in the browser.
        (added.querySelector('.field-applicationname') as HTMLElement).textContent = name;
        grid.appendChild(added);
        one('.mcp-empty')?.remove();

        // Advance the footer row to the next index and clear the picker.
        const next = index + 1;
        row.setAttribute('data-id', String(next));
        reindex(row, '.field-appgroupid', 'AppGroupId', next);
        reindex(row, '.field-applicationid', 'ApplicationId', next);
        reindex(row, '.field-resourcetype', 'ResourceType', next);
        picker.value = '';
        return false;
    },

    /** Drop a row that was added in this editing session and never persisted. */
    RemoveRow(rowSelector: string): void {
        for (const node of all(rowSelector)) node.remove();
    },
};

/** Both save buttons POST their form and swap whatever comes back into place. */
function save(container: string, url: string, formId: string, alertInto: string, alertTitle: string): false {
    preventBlankRowPost();
    const form = one(formId) as HTMLFormElement | null;
    if (!form || !isValid(form)) return false;
    void ajaxSwap({
        container,
        url,
        method: 'POST',
        data: serialize(form),
        mode: 'replaceWith',
        busy: '#AppGroupSaveChangesLoading',
        alertInto,
        alertTitle,
        // The POST answers with the Edit partial on success and the Create
        // partial (carrying ModelState) on failure, and replaceWith drops the
        // parsed form, so validation has to be re-attached to whichever landed.
        onSuccess: () => attach('#EditAppGroupForm, #CreateAppGroupForm'),
    });
    return false;
}

export const Create = {
    SaveButtonClick(): false {
        return save('AppGroupCreateContainer', 'AppGroup', 'CreateAppGroupForm',
            'AppGroupCreateAlertContainer', 'Failed to create the Application Group');
    },
};

export const Edit = {
    SaveChangesButtonClick(): false {
        return save('AppGroupEditContainer', 'AppGroup/Edit', 'EditAppGroupForm',
            'AppGroupSaveChangesAlertContainer', 'Failed to save the Application Group');
    },
};

/**
 * Re-render the list for whichever Domain the picker is on. Both the picker's
 * change and a completed delete want exactly this, and a delete must not assume
 * the picker is on the default Domain.
 */
function reloadList(): void {
    const form = one('DomainSelectionForm') as HTMLFormElement | null;
    void ajaxSwap({
        container: 'AppGroupListContainer',
        url: 'AppGroup/List',
        method: 'POST',
        data: form ? serialize(form) : [],
        alertTitle: 'Failed to load Application Groups for that Domain',
    });
}

export const List = {
    /** A list row opens the group for editing. */
    RowClick(id: number | string): void {
        void ajaxModal({
            modal: 'AppGroupModal',
            url: `AppGroup/Edit/${id}`,
            formId: 'EditAppGroupForm',
            focusId: 'Name',
            errorTitle: `Application Group ${id}`,
            errorMessage: 'Application Group Edit failed to execute:',
        });
    },

    /**
     * The new group is seeded with the domain the LIST is showing, not the
     * rail's current domain: creating from a list filtered to another domain
     * put the group somewhere the operator was not looking, and it then did
     * not appear in the list they had just added it to. The rail's domain is
     * the fallback for the <Global> selection, which no group can belong to.
     */
    CreateButtonClick(): void {
        const domainId = value('SelectedDomainIdDropList') || value('CurrentDomainId');
        void ajaxModal({
            modal: 'AppGroupModal',
            // The route is Create/{domainId:int} -- a ?domainId= query matches nothing.
            url: `AppGroup/Create/${domainId}`,
            formId: 'CreateAppGroupForm',
            focusId: 'Name',
            errorTitle: 'New Application Group',
            errorMessage: 'New Application Group View failed to execute:',
        });
    },

    /**
     * AppGroup/Delete is a DELETE, so there is no navigation to pre-empt: issue
     * the XHR on a confirm and re-render the list in place. Returning false
     * still suppresses the row's own click default, since the button sits
     * inside a data-action <tr>.
     */
    DeleteClick(appGroupId: number | string): false {
        void confirm({
            title: 'Delete Application Group',
            message: 'Delete this Application Group?',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `AppGroup/Delete?appGroupId=${appGroupId}`,
                    method: 'DELETE',
                    onSuccess: () => { reloadList(); },
                    onFailure: failToast('Failed to delete the Application Group'),
                });
            },
        });
        return false;
    },

    /** Re-render the list for the chosen Domain. */
    DomainChanged(): void {
        reloadList();
    },
};
